import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Box } from '@mui/material';
import SearchField from 'src/shared/components/form/searchField';
import { MemberDataGrid } from 'src/modules/member/components/memberDataGrid';
import { MemberNumberSearch } from 'src/shared/components/form/memberNumberSearch';
import { RoutesEnum } from 'src/shared/models/enums/routes.enum';
import { useLocalStorage } from 'src/shared/hooks/useLocalStorage';
import { LocalStorageKeys } from 'src/shared/models/enums/localStorageKeys.enum';
import { MemberStatusTabKeysEnum } from 'src/shared/models/enums/memberStatusTabKeys.enum';
import { PageFilterSection } from 'src/shared/components/organisms/pageFilterSection';
import { DEFAULT_PAGE_INDEX, DEFAULT_PAGE_SIZE } from 'src/shared/models/enums/gridFilterConfig';
import getDefaultMemberSearchFormState, { MemberSearchFormState } from '../models/memberSearchFormState';
import { MemberOverviewStatuses } from '../components/memberOverviewStatuses';
import { memberTabs } from '../models/memberTabs';
import { ControlledMultiDistrictPicker } from '../../../shared/components/form/controlledMultiDistrictPicker';

type MembersListPageProps = {
  otherContactMode: boolean;
};

export const MembersListPage = (props: MembersListPageProps) => {
  const { otherContactMode } = props;
  const navigate = useNavigate();
  const { t } = useTranslation(['common']);

  const [searchFilterInitialized, setSearchFilterInitialized] = useState<boolean>(false);
  const [gridLoading, setGridLoading] = useState<boolean>(false);
  const [textSearchTriggered, setTextSearchTriggered] = useState<boolean>(false);
  const prevGridLoadingRef = useRef(false);

  const initialState = getDefaultMemberSearchFormState() as MemberSearchFormState;
  const [advancedSearchFilter, setAdvancedSearchFilter] = useLocalStorage<MemberSearchFormState>(
    otherContactMode ? LocalStorageKeys.OtherContactOverviewFilter : LocalStorageKeys.MemberOverviewFilter,
    {
      ...initialState,
      associationIds: [],
      districtIds: [],
      searchString: '',
      otherContact: otherContactMode,
      status: memberTabs.filter((x) => x.key === MemberStatusTabKeysEnum.Paid || x.key === MemberStatusTabKeysEnum.NotPaid),
    } as unknown as MemberSearchFormState,
  );

  const formMethods = useForm<MemberSearchFormState>({
    defaultValues: {
      ...initialState,
      associationIds: '',
      districtIds: '',
      postCode: '',
      municipalityCode: [],
      otherContact: `${otherContactMode}`,
      status: [],
    },
  });

  const { handleSubmit, reset } = formMethods;

  const formRef = useRef<HTMLFormElement>(null);

  const searchFilter: MemberSearchFormState = useMemo(() => {
    return {
      pageSize: advancedSearchFilter?.pageSize ?? DEFAULT_PAGE_SIZE,
      pageIndex: advancedSearchFilter?.pageIndex ?? DEFAULT_PAGE_INDEX,
      sortCol: advancedSearchFilter?.sortCol ?? 'memberNr',
      sortDir: advancedSearchFilter?.sortDir ?? 'desc',
      associationIds: advancedSearchFilter?.associationIds || [],
      districtIds: advancedSearchFilter?.districtIds || [],
      searchString: advancedSearchFilter?.searchString,
      status: advancedSearchFilter?.status,
      otherContact: otherContactMode,
    } as unknown as MemberSearchFormState;
  }, [
    advancedSearchFilter?.pageSize,
    advancedSearchFilter?.pageIndex,
    advancedSearchFilter?.sortCol,
    advancedSearchFilter?.sortDir,
    advancedSearchFilter?.associationIds,
    advancedSearchFilter?.districtIds,
    advancedSearchFilter?.searchString,
    advancedSearchFilter?.status,
    otherContactMode,
  ]);

  useEffect(() => {
    // Only execute on the initial load to prevent unnecessary resets
    if (!searchFilterInitialized) {
      const filter: MemberSearchFormState = {
        ...initialState,
      };

      // Populate `filter` only with non-null, non-undefined values from `advancedSearchFilter`
      let countSearchParams = 0;
      Object.keys(advancedSearchFilter).forEach((key) => {
        const value = advancedSearchFilter[key];
        // Check if value is valid (non-empty array, non-empty string, or defined)
        const isValid = Array.isArray(value)
          ? value.length > 0 // Valid if array and not empty
          : value !== undefined && value !== null && value !== ''; // Valid if not undefined, null, or empty string

        if (isValid) {
          filter[key] = value;
          countSearchParams += 1;
        }
      });
      reset(filter); // Reset form with the filtered values
      setSearchFilterInitialized(true); // Mark initialization as complete to avoid re-running this effect
    }
  }, [advancedSearchFilter, initialState, reset, searchFilterInitialized]);

  const onSubmit = (data, e) => {
    e.preventDefault();
    setAdvancedSearchFilter(data);
  };

  useEffect(() => {
    if (prevGridLoadingRef.current && !gridLoading && textSearchTriggered) {
      setTextSearchTriggered(false);
    }
    prevGridLoadingRef.current = gridLoading;
  }, [gridLoading, textSearchTriggered]);

  const handleDistrict = useCallback(
    (x: any) => {
      setAdvancedSearchFilter((prev) => ({ ...prev, ...{ districtIds: x } }));
    },
    [setAdvancedSearchFilter],
  );

  return (
    <>
      <FormProvider {...formMethods}>
        <form onSubmit={handleSubmit(onSubmit)} ref={formRef}>
          <PageFilterSection
            leftContent={
              searchFilterInitialized ? (
                <Box
                  sx={{
                    display: 'grid',
                    gap: 1,
                    alignItems: 'center',
                    width: '100%',
                    gridTemplateColumns: otherContactMode
                      ? { xs: '1fr', lg: '1fr 1fr', xl: '1fr 1fr 1fr' }
                      : { xs: '1fr', lg: '1fr 1fr', xl: '1fr auto 1fr auto' },
                  }}
                >
                  <SearchField
                    initialValue={advancedSearchFilter.searchString || ''}
                    debounceTime={1500}
                    placeholder={t('common:form.searchPlaceholder')}
                    showSearchButton
                    fullWidth
                    isSearching={textSearchTriggered && gridLoading}
                    tooltipText={t('common:form.searchTooltip')}
                    onSearch={(newSearchString) => {
                      setTextSearchTriggered(true);
                      setAdvancedSearchFilter((prev) => ({
                        ...prev,
                        searchString: newSearchString,
                      }));
                    }}
                  />
                  {!otherContactMode && (
                    <MemberOverviewStatuses
                      otherContactMode={otherContactMode}
                      initialValue={advancedSearchFilter.status || []}
                      callback={(val) => {
                        setAdvancedSearchFilter((prev) => ({ ...prev, ...{ status: val } }));
                      }}
                    />
                  )}
                  <ControlledMultiDistrictPicker name='districtIds' callback={(x) => handleDistrict(x)} />
                  <MemberNumberSearch onSelect={(memberId) => navigate(`${RoutesEnum.MemberDetails}/${memberId}`)} />
                </Box>
              ) : (
                <></>
              )
            }
          />
        </form>
      </FormProvider>
      {searchFilterInitialized && searchFilter && (
        <MemberDataGrid
          otherContactMode={otherContactMode}
          searchFilter={searchFilter}
          searchFilterCallback={(newFilter) => {
            setAdvancedSearchFilter((prev) => ({
              ...prev,
              ...newFilter,
            }));
          }}
          loadingCallback={setGridLoading}
          checkboxSelection
        />
      )}
    </>
  );
};
